/**
 * Plan pricing, capacities and the guarantees that do not vary by tier.
 *
 * **This module is the single source for every number that appears beside a price.**
 * `website-handover-prompt.md` §3 states the obligation and the reason:
 *
 *   > "Plan names, prices, GTIN counts, fair-use ceilings and feature flags appear on the
 *   >  home page teaser, the pricing cards, the comparison table, the FAQ **and** in the
 *   >  app's billing screen. Put them in one structured file and render all five surfaces
 *   >  from it."
 *
 * Four of those five are in `apps/site`. The fifth — the dashboard's billing screen — had
 * no route when this module landed, which is precisely why it landed in `shared` rather than
 * in the site: **the surface that did not exist yet was the one that would otherwise retype
 * these numbers.** It has existed since U57, at `/settings/billing`, and imports this
 * module. A constant written once and imported twice cannot drift; a number typed in two
 * places has already drifted and nobody has noticed.
 *
 * ## Transcribed from `screens.md` §9, and locked
 *
 * The values are read off the design handoff's pricing line, which agrees with
 * `Business/Pricing Model.md` and `Business/Business Plan.md` §6 on every figure. Because
 * this is a transcription of a spec rather than a derivation, `test/plans.test.ts` freezes
 * it: changing a price here turns that suite red and names the row, so a pricing change is
 * a deliberate act with a review attached. That is the same discipline
 * `apps/resolver/test/contract-table.ts` carries, applied to the other frozen table in the
 * product.
 *
 * ## There is no API-call field, and its absence is the mechanism
 *
 * `open-decisions.md` #13: the 100,000 API-call ceiling that appears in some drafts **is
 * invented**, and nothing may put a denominator on a public page until it is real. A
 * comment asking the next person to remember that is not a mechanism. **A type with no
 * field for it is** — the number cannot be rendered because it cannot be expressed, and
 * adding it back is an edit to this file that a reviewer sees.
 *
 * ## Seats are not here either, for the opposite reason
 *
 * `screens.md` §10's gated-states line is *"API on Growth, role choice on Growth, **seats
 * free everywhere**"*, and `Business Plan.md`:177 gives Launch "unlimited seats". Seats are
 * therefore not a plan dimension at all, so they get no field — see {@link UNIVERSAL} for
 * where that fact is stated instead. The website design's `1 / 3 / 10 / ∞ / ∞` comparison
 * row contradicted all three documents and was ruled out on 2026-08-12 (R-2.5-3).
 */
import {} from "./tiers.js";
/**
 * The plan table. Cheapest first, in `tiers.ts`'s `TIERS` order.
 *
 * Fair use is fair use, and the distinction is load-bearing in copy: exceeding it never
 * stops a printed code resolving and never changes a machine route.
 *
 * Publishing, scoping, correcting and lifting a recall notice is available at every tier,
 * in every billing state, and is never throttled or capped. A recalled GTIN keeps its
 * organisation's billing status: the notice renders above a degraded page — Drift §U60.
 */
export const PLANS = {
    launch: {
        monthlyUsd: 0,
        yearlyUsd: null,
        activeGtins: 10,
        pooledGtins: false,
        fairUsePerMonth: 25_000,
    },
    starter: {
        monthlyUsd: 49,
        yearlyUsd: 490,
        activeGtins: 100,
        pooledGtins: false,
        fairUsePerMonth: 250_000,
    },
    growth: {
        monthlyUsd: 199,
        yearlyUsd: 1_990,
        activeGtins: 1_000,
        pooledGtins: false,
        fairUsePerMonth: 1_000_000,
    },
    scale: {
        monthlyUsd: 599,
        yearlyUsd: 5_990,
        activeGtins: 10_000,
        pooledGtins: false,
        fairUsePerMonth: 5_000_000,
    },
    enterprise: {
        monthlyUsd: null,
        yearlyUsd: null,
        fromUsd: 1_500,
        activeGtins: 10_000,
        pooledGtins: true,
        fairUsePerMonth: null,
    },
};
/**
 * Extra GTIN capacity, sold in blocks.
 *
 * `screens.md` §9: *"Overage +$10 per 25-GTIN block."* The 80% alert is in the same
 * handoff's billing screen and in `Business Plan.md`:183; it is here because a surface
 * describing the blocks should be able to state when the warning arrives without inventing
 * a threshold.
 *
 * Launch has no overage: the upgrade path from ten GTINs is Starter, and selling $10 blocks
 * against a $0 plan is incoherent. No block is charged yet, so the site states none —
 * Decisions/2026-10-29 The GTIN Overage Charge Is Deferred.
 */
export const OVERAGE = {
    gtinBlockSize: 25,
    gtinBlockUsdPerMonth: 10,
    alertAtPercentOfLimit: 80,
};
/**
 * How many months an annual plan is billed as.
 *
 * `screens.md` §9 is explicit that annual is presented as **"two months free", never a
 * percentage** — 10/12 is 16.67%, and a discount rendered as a rounded percentage is a
 * number nobody can check against the two prices printed beside it. Exported so that the
 * relationship is asserted rather than assumed: `test/plans.test.ts` checks every tier
 * carrying both prices satisfies `yearly === monthly * YEARLY_BILLED_MONTHS`.
 */
export const YEARLY_BILLED_MONTHS = 10;
/**
 * The guarantees that are identical at every tier, including Launch.
 *
 * **These are the pricing argument, and they are data so that they can be tested.** The
 * design renders them as a green band above every varying row, and
 * `website-handover-prompt.md` §3 states the constraint plainly:
 *
 *   > "The four rows in the comparison table's green band (**identical at every tier**) are
 *   >  the pricing argument. They come from rulings #2 and #3 and must not be quietly moved
 *   >  into a paid column later without a product decision."
 *
 * *Quietly* is the word the test attacks. A row moved into a paid column is a one-line diff
 * on a marketing page that no reviewer would necessarily read as a product change; moving
 * one here is a diff in `packages/shared` that turns a suite red and names what moved.
 *
 * **Exactly four, and seats are deliberately not a fifth.** Seats are free everywhere
 * (`screens.md` §10) and that is worth stating on the comparison table — but this band's
 * subtitle is *"correctness is not a feature"*, and it derives from rulings #2 and #3. A
 * capacity fact admitted here on the grounds that it is also universal would dilute the
 * band into "things that happen not to vary", which is how the four that matter stop being
 * conspicuous. Seats render as an ordinary all-tiers row instead.
 */
export const UNIVERSAL = [
    {
        id: "vector-artwork",
        label: "Print-ready vector artwork, no watermark",
        // Ruling 3 is a trust gate, not a price gate, and the qualifier is not optional:
        // unqualified, this row contradicts the "two things no plan will ever buy you"
        // section two screens below it on the same page.
        note: "Once your company prefix is verified",
    },
    {
        id: "preflight",
        label: "Full preflight — quiet zone, X-dimension, POS minimums",
        note: null,
    },
    {
        id: "recall-notice",
        // "Publish a recall notice", never "publish a recall". The word `notice` is a legal
        // scope statement: `gs1:recallStatus` reaches people who scan the code and nobody
        // else. `check:site-copy` refuses the shorter spelling anywhere in the site tree.
        label: "Publish a recall notice",
        note: null,
    },
    {
        id: "resolves-after-cancellation",
        label: "Codes keep resolving after cancellation",
        note: null,
    },
];
/**
 * The annual price of a tier, or `null` where no annual plan exists.
 *
 * A function rather than a field read, because the surfaces that need it are toggling
 * between two presentations of the same tier and should ask one question rather than
 * branch on `null` in four places.
 */
export function yearlyPrice(tier) {
    return PLANS[tier].yearlyUsd;
}
/**
 * Whether a tier is quoted rather than listed.
 *
 * Enterprise is set administratively after a sales conversation — `tiers.ts` records that
 * `SELF_SERVE_TIERS` exists for the same reason. A surface that renders a checkout-shaped
 * control for a quoted tier implies a self-serve path that does not exist, and the first
 * customer to click it has a bad time.
 */
export function isQuotedTier(tier) {
    return PLANS[tier].monthlyUsd === null;
}
//# sourceMappingURL=plans.js.map